import { EMPTY_LOQATE_ADDRESS, IInputLoqateValue } from '@common/general-ui/input-loqate/input-loqate.component';
import { IUseCustomFormReturn } from '@common/hooks/use-form.hook';
import { useStack } from '@common/hooks/use-stack.hook';
import { createCollection } from '@common/utils/create-collection.util';
import { customNotify } from '@common/utils/custom-notify.util';
import { dataLayer } from '@common/utils/data-layer.util';
import { ILinkItem } from '@common/utils/make-links/make-links.util';
import { apiHooks } from '@src/api/hooks/api-hooks';
import { createDeliveryConfigDto } from '@src/api/model/delivery-config.dto';
import { IRegisterModel } from '@src/api/model/register.model';
import { ITabChangeEventHandler } from '@src/features/register/register-layout.component';
import { useRegister } from '@src/features/register/register.hook';
import { LINKS } from '@src/shared/links.constant';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { CheckDeliveryComponent, IAddressFormProps } from '../component/check-delivery.component';

export interface IFormProps {
    firstName: string;
    lastName: string;
    safePlace: string;
    deliveryDate: string;
    email: string;
    phoneNumber: string;
}

export const CheckDeliveryContainer = (): JSX.Element => {
    const { registerInfo, saveToRegister } = useRegister();
    const [form, setForm] = useState<IUseCustomFormReturn<IAddressFormProps>>();
    const history = useHistory();
    const [pushUrl, popUrl] = useStack<ILinkItem>();
    const [showPostCodeNotServed, setShowPostCodeNotServed] = useState<boolean>(false);

    const {
        mutateAsync,
        isSuccess: validatePostcodeSuccess,
        isLoading: validatePostcodeLoading,
        reset: validateReset,
    } = apiHooks.packHouse.useValidatePostCode();

    useEffect(() => {
        if (validatePostcodeSuccess) {
            saveToRegister({ isCheckPostCodeStepValid: true });
            let url: string;
            if (!registerInfo.isFirstBoxStepValid) {
                url = LINKS.register.firstBox.toString();
            } else if (!registerInfo.isAccountDetailsStepValid) {
                url = LINKS.register.accountDetails.toString();
            } else if (!registerInfo.isDeliveryStepValid) {
                url = LINKS.register.deliveryDetails.toString();
            } else {
                url = LINKS.register.payment.toString();
            }
            history.push(popUrl()?.toString() || url);
        }
    }, [
        validatePostcodeSuccess,
        saveToRegister,
        history,
        popUrl,
        registerInfo.isAccountDetailsStepValid,
        registerInfo.isFirstBoxStepValid,
        registerInfo.isDeliveryStepValid,
    ]);

    const handleAddressCheck = async (address: IInputLoqateValue): Promise<void> => {
        await mutateAsync({ post_code: address.postCode.toUpperCase() })
            .then((res) => {
                if (res.valid) {
                    const deliveryConfigs = createCollection(
                        (res.list || []).map((e) => createDeliveryConfigDto(e)) || [],
                        0,
                        {},
                    );
                    const activeDeliveryConfigs = deliveryConfigs?.list?.filter((f) => f.active && !f.hidden);
                    const updatedDeliveryConfig = activeDeliveryConfigs?.length ? activeDeliveryConfigs[0] : undefined;

                    if (updatedDeliveryConfig) {
                        messageReset();
                        saveToRegister({ deliveryConfigId: updatedDeliveryConfig.id, shippingAddress: address });
                    }
                }
            })
            .catch((err) => {
                messageReset();
                saveToRegister({ isCheckPostCodeStepValid: false });
                if (err.error === 'PostCodeNotServed') {
                    setShowPostCodeNotServed(true);
                    dataLayer.createGenericEvent(
                        'Error',
                        'PostCodeCheck_Delivery-Tab',
                        'PostCodeNotServed',
                        postcodeRef.current,
                    );
                } else {
                    customNotify.error([{ beError: err, onShown: validateReset }]);
                }
            });
    };

    const handleAddressChange = useCallback(
        (address: IInputLoqateValue) => {
            if (registerInfo.isCheckPostCodeStepValid) {
                if (address === EMPTY_LOQATE_ADDRESS) {
                    // on shipping address change --> reset all box-related, address-related register info
                    messageReset();
                    saveToRegister({
                        shippingAddress: EMPTY_LOQATE_ADDRESS,
                        partialAddress: true,
                        isDeliveryStepValid: false,
                        deliveryConfigId: undefined,
                        deliveryDate: undefined,
                        startDate: undefined,
                    });
                }
                return;
            }
        },
        [saveToRegister, registerInfo.isCheckPostCodeStepValid],
    );

    const postcodeRef = useRef(registerInfo.shippingAddress.postCode);

    useEffect(() => {
        postcodeRef.current = registerInfo.shippingAddress.postCode;
    }, [registerInfo.shippingAddress.postCode]);

    const messageReset = (): void => {
        setShowPostCodeNotServed(false);
    };

    const handleFormUpdate = useCallback(
        (data: Partial<IRegisterModel>): void => {
            saveToRegister({ ...data });
        },
        [saveToRegister],
    );

    // GTM checkout event
    useEffect(() => {
        const step = 'Step 1 - Check Delivery';
        dataLayer.createCheckoutEnhancedEcommerceEvent(step);
    }, []);

    const handleTabChange = useCallback<ITabChangeEventHandler>(
        (arg) => {
            if (arg.newTab.href) {
                pushUrl(arg.newTab.href);
            }
            form?.isValid ? form.submit() : form?.setBlurForAllFields();
        },
        [pushUrl, form],
    );

    return (
        <CheckDeliveryComponent
            isLoading={validatePostcodeLoading}
            onFormInit={setForm}
            onAddressCheck={handleAddressCheck}
            handleAddressChange={handleAddressChange}
            onFormUpdate={handleFormUpdate}
            onTabChange={handleTabChange}
            registerInfo={registerInfo}
            saveToRegister={saveToRegister}
            showPostCodeNotServed={showPostCodeNotServed}
        />
    );
};
